import React, { useState } from 'react';
import { SeoHead } from '../components/SeoHead';
import { ChevronRight, Copy, Check, Zap, Shield, Book, Code2, Database, Lock, Webhook } from 'lucide-react';

const BASE = process.env.REACT_APP_BACKEND_URL || 'https://app.shangrilux.com';

const HMAC_PYTHON = `import hmac
import hashlib
from fastapi import Request, HTTPException

WEBHOOK_SECRET = "il_tuo_secret_shangrilux"  # da salvare in .env

async def verify_shangrilux_signature(request: Request) -> dict:
    """Verifica la firma HMAC-SHA256 del webhook Shangrilux."""
    signature_header = request.headers.get("X-Shangrilux-Signature", "")
    event_type = request.headers.get("X-Shangrilux-Event", "")

    body = await request.body()

    # Calcola HMAC-SHA256 del body raw con il tuo secret
    expected = "sha256=" + hmac.new(
        WEBHOOK_SECRET.encode("utf-8"),
        body,
        hashlib.sha256
    ).hexdigest()

    if not hmac.compare_digest(expected, signature_header):
        raise HTTPException(status_code=401, detail="Firma webhook non valida.")

    import json
    payload = json.loads(body)
    return payload

# Endpoint FastAPI di esempio
from fastapi import APIRouter
router = APIRouter()

@router.post("/webhook/shangrilux")
async def receive_webhook(request: Request):
    payload = await verify_shangrilux_signature(request)
    event = payload.get("event")

    if event == "booking.created":
        booking = payload["data"]
        print(f"Nuova prenotazione: {booking['guest_name']} → {booking['property_id']}")
        # Sincronizza con il tuo PMS qui...

    elif event == "booking.confirmed":
        print(f"Prenotazione confermata: {payload['data']['id']}")

    return {"received": True}`;

const HMAC_NODE = `const express = require('express');
const crypto = require('crypto');

const WEBHOOK_SECRET = process.env.SHANGRILUX_SECRET; // da .env

// Middleware: verifica firma HMAC-SHA256
function verifyShangriluxSignature(req, res, next) {
  const signatureHeader = req.headers['x-shangrilux-signature'] || '';
  const rawBody = req.rawBody; // richiede bodyParser con verify

  if (!rawBody) {
    return res.status(400).json({ error: 'Body grezzo non disponibile.' });
  }

  const expected = 'sha256=' + crypto
    .createHmac('sha256', WEBHOOK_SECRET)
    .update(rawBody)
    .digest('hex');

  // Usa timingSafeEqual per prevenire timing attacks
  const sigBuffer = Buffer.from(signatureHeader);
  const expBuffer = Buffer.from(expected);

  if (sigBuffer.length !== expBuffer.length ||
      !crypto.timingSafeEqual(sigBuffer, expBuffer)) {
    return res.status(401).json({ error: 'Firma webhook non valida.' });
  }

  next();
}

const app = express();

// IMPORTANTE: usa il middleware con il body raw per l'HMAC
app.use('/webhook/shangrilux', express.json({
  verify: (req, res, buf) => { req.rawBody = buf; }
}));

app.post('/webhook/shangrilux', verifyShangriluxSignature, (req, res) => {
  const { event, data, tenant } = req.body;

  switch (event) {
    case 'booking.created':
      console.log(\`Nuova prenotazione da \${tenant}:\`, data.guest_name);
      // Sincronizza con PMS...
      break;
    case 'booking.confirmed':
      console.log(\`Confermata prenotazione:\`, data.id);
      break;
    case 'access.command':
      console.log(\`Comando accesso:\`, data.command, 'su', data.device);
      break;
  }

  res.json({ received: true });
});

app.listen(3000);`;

const ENDPOINTS = [
  {
    group: 'Informazioni',
    color: '#e5d4a1',
    items: [
      {
        method: 'GET', path: '/api/v1/', desc: 'Info sull\'API e sul tenant autenticato.',
        response: `{
  "api": "Shangrilux Public API",
  "version": "1.0",
  "tenant": "villa-aurora",
  "plan": "professional",
  "rate_limit_per_hour": 2000,
  "endpoints": { "properties": "/api/v1/properties", ... }
}`,
      },
    ],
  },
  {
    group: 'Proprietà',
    color: '#c9a84c',
    items: [
      {
        method: 'GET', path: '/api/v1/properties', desc: 'Lista tutte le proprietà del tenant.',
        response: `{
  "data": [
    {
      "id": "64a1b2c3d4e5f6a7b8c9d0e1",
      "name": "Villa Belvedere",
      "slug": "villa-belvedere",
      "city": "Firenze",
      "max_guests": 8,
      "checkin_time": "15:00",
      "checkout_time": "11:00",
      "currency": "EUR",
      "is_active": true
    }
  ],
  "total": 1
}`,
      },
      {
        method: 'GET', path: '/api/v1/properties/{id}', desc: 'Dettaglio singola proprietà. Accetta ID MongoDB o slug.',
        response: `{ "data": { ... } }`,
      },
    ],
  },
  {
    group: 'Prenotazioni',
    color: '#c9a84c',
    items: [
      {
        method: 'GET', path: '/api/v1/bookings', desc: 'Lista prenotazioni. Query: ?status=pending|confirmed|cancelled&limit=50&offset=0',
        response: `{
  "data": [
    {
      "id": "...",
      "guest_name": "Mario Rossi",
      "guest_email": "mario@hotel.it",
      "checkin_at": "2026-06-01",
      "checkout_at": "2026-06-05",
      "status": "confirmed",
      "source": "pms",
      "external_id": "RES-001234"
    }
  ],
  "total": 42,
  "has_more": false
}`,
      },
      {
        method: 'GET', path: '/api/v1/bookings/{id}', desc: 'Dettaglio singola prenotazione. Accetta ID MongoDB o external_id.',
        response: `{ "data": { ... } }`,
      },
      {
        method: 'POST', path: '/api/v1/bookings', desc: 'Crea una prenotazione da PMS o channel manager. Deduplicazione automatica per external_id.',
        body: `{
  "property_id": "villa-belvedere",
  "guest_name": "Mario Rossi",
  "guest_email": "mario@email.it",
  "guest_phone": "+39 333 1234567",
  "num_guests": 2,
  "checkin_at": "2026-06-01",
  "checkout_at": "2026-06-05",
  "total_price": 1200.00,
  "source": "pms",
  "external_id": "RES-001234",
  "notes": "Arrivo tardivo (dopo le 20)"
}`,
        response: `{
  "data": { "id": "...", "status": "pending", ... },
  "created": true
}`,
      },
      {
        method: 'PATCH', path: '/api/v1/bookings/{id}/cancel', desc: 'Cancella una prenotazione attiva.',
        response: `{ "data": { "status": "cancelled", ... } }`,
      },
    ],
  },
  {
    group: 'Accessi & Dispositivi',
    color: '#a67c52',
    items: [
      {
        method: 'GET', path: '/api/v1/access/devices', desc: 'Lista tutti i dispositivi di accesso (serrature, lettori card, ecc.).',
        response: `{
  "data": [
    {
      "id": "...",
      "name": "Porta Principale",
      "slug": "porta-principale",
      "type": "lock",
      "brand": "Nuki",
      "status": "locked",
      "is_online": true,
      "battery_level": 85
    }
  ],
  "total": 3
}`,
      },
      {
        method: 'GET', path: '/api/v1/access/devices/{id}', desc: 'Stato e dettaglio di un dispositivo. Accetta ID o slug.',
        response: `{ "data": { ... } }`,
      },
      {
        method: 'POST', path: '/api/v1/access/devices/{id}/command', desc: 'Invia un comando al dispositivo. Il comando viene registrato nel log accessi.',
        body: `{
  "command": "unlock",
  "pin": "1234",
  "duration_seconds": 10
}`,
        response: `{
  "data": { "status": "unlocked", ... },
  "command": "unlock",
  "executed_at": "2026-06-01T14:00:00Z",
  "success": true
}`,
      },
    ],
  },
];

const METHOD_COLORS = {
  GET: { bg: 'rgba(99,102,241,0.12)', text: '#818cf8', border: 'rgba(99,102,241,0.25)' },
  POST: { bg: 'rgba(16,185,129,0.12)', text: '#34d399', border: 'rgba(16,185,129,0.25)' },
  PATCH: { bg: 'rgba(245,158,11,0.12)', text: '#fbbf24', border: 'rgba(245,158,11,0.25)' },
  DELETE: { bg: 'rgba(239,68,68,0.1)', text: '#f87171', border: 'rgba(239,68,68,0.2)' },
};

export default function DevelopersPage() {
  const [copied, setCopied] = useState(null);
  const [openEndpoint, setOpenEndpoint] = useState(null);

  const copy = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopied(id);
    setTimeout(() => setCopied(null), 1800);
  };

  const curlExample = (item) => {
    const headers = `-H "X-API-Key: ostx_your_api_key"`;
    if (item.method === 'GET') {
      return `curl ${headers} \\\n     ${BASE}${item.path}`;
    }
    return `curl -X ${item.method} ${headers} \\\n     -H "Content-Type: application/json" \\\n     -d '${item.body || '{}'}' \\\n     ${BASE}${item.path}`;
  };

  return (
    <div style={{ background: '#080808', color: '#e5e5e5', fontFamily: "'DM Sans', sans-serif", minHeight: '100vh' }}>
      <SeoHead page="developers" lang="it" theme="dark" />

      {/* Nav */}
      <div className="sticky top-0 z-40 border-b" style={{ background: 'rgba(8,8,8,0.96)', backdropFilter: 'blur(20px)', borderColor: '#1a1a1a' }}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
            <span style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: '18px', fontWeight: 300, letterSpacing: '0.05em', color: '#e5e5e5' }}>Shangrilux</span>
            <ChevronRight size={13} style={{ color: 'rgba(229,229,229,0.3)' }} />
            <span className="text-sm" style={{ color: '#c9a84c' }}>API Reference</span>
          </a>
          <div className="flex items-center gap-4">
            <a href="/blog" className="text-xs" style={{ color: 'rgba(229,229,229,0.5)' }}>Guide</a>
            <a href="/#demo" className="text-sm px-4 py-2 rounded-sm font-medium" style={{ background: '#c9a84c', color: '#000' }}>Demo Gratuita</a>
          </div>
        </div>
      </div>

      {/* Hero */}
      <div className="max-w-7xl mx-auto px-6 pt-20 pb-14">
        <p className="text-xs tracking-[5px] uppercase mb-5" style={{ color: '#c9a84c' }}>Developer Docs</p>
        <h1 style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: 'clamp(36px,5vw,60px)', fontWeight: 300, lineHeight: 1.1, marginBottom: '16px' }}>
          Shangrilux Public API.<br />
          <span style={{ color: '#c9a84c' }}>Integra tutto.</span>
        </h1>
        <p className="max-w-xl text-base mb-10" style={{ color: 'rgba(229,229,229,0.5)', lineHeight: 1.7 }}>
          REST API v1 per connettere PMS, sistemi di controllo accessi, channel manager e qualsiasi piattaforma hospitality con Shangrilux.
        </p>

        {/* Feature Pills */}
        <div className="flex flex-wrap gap-3">
          {[
            { icon: Zap, label: 'Fino a 10.000 req/ora (Elite)' },
            { icon: Shield, label: 'Autenticazione API Key' },
            { icon: Database, label: 'Multi-tenant nativo' },
            { icon: Lock, label: 'TLS obbligatorio' },
          ].map(({ icon: Icon, label }) => (
            <div key={label} className="flex items-center gap-2 px-3 py-1.5 rounded-full text-xs"
              style={{ background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.2)', color: 'rgba(229,229,229,0.6)' }}>
              <Icon size={11} style={{ color: '#c9a84c' }} /> {label}
            </div>
          ))}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 pb-20 grid grid-cols-1 lg:grid-cols-4 gap-10">

        {/* Sidebar */}
        <div className="lg:col-span-1">
          <div className="sticky top-24">
            <p className="text-[10px] uppercase tracking-[3px] mb-4" style={{ color: 'rgba(229,229,229,0.3)' }}>Sezioni</p>
            <nav className="space-y-1">
              {[
                { label: 'Autenticazione', href: '#auth' },
                { label: 'Rate Limiting', href: '#rate-limits' },
                { label: 'Errori', href: '#errors' },
                { label: 'Webhook & HMAC', href: '#webhooks' },
                { label: 'SDK Ufficiali', href: '#sdk' },
                ...ENDPOINTS.map(g => ({ label: g.group, href: `#${g.group.toLowerCase().replace(/\s+/g, '-')}` })),
              ].map(({ label, href }) => (
                <a key={href} href={href} className="block text-sm py-1.5 transition-colors"
                  style={{ color: 'rgba(229,229,229,0.45)' }}
                  onMouseEnter={e => e.currentTarget.style.color = '#c9a84c'}
                  onMouseLeave={e => e.currentTarget.style.color = 'rgba(229,229,229,0.45)'}>
                  {label}
                </a>
              ))}
            </nav>
          </div>
        </div>

        {/* Content */}
        <div className="lg:col-span-3 space-y-12">

          {/* Auth */}
          <section id="auth">
            <h2 className="text-lg font-medium mb-4" style={{ color: '#e5e5e5' }}>Autenticazione</h2>
            <p className="text-sm mb-4" style={{ color: 'rgba(229,229,229,0.55)', lineHeight: 1.7 }}>
              Tutte le richieste devono includere l'header <code style={{ color: '#c9a84c' }}>X-API-Key</code> con la tua chiave API. Le chiavi sono generate dal pannello admin del tuo tenant (Impostazioni → API Keys).
            </p>
            <div className="rounded-sm overflow-hidden" style={{ border: '1px solid #1e1e1e' }}>
              <div className="flex items-center justify-between px-4 py-2.5" style={{ background: '#111' }}>
                <span className="text-[10px] uppercase tracking-[3px]" style={{ color: 'rgba(229,229,229,0.3)' }}>HTTP Header</span>
                <button onClick={() => copy('X-API-Key: ostx_your_api_key_here', 'auth')}
                  className="flex items-center gap-1 text-[10px]" style={{ color: 'rgba(229,229,229,0.35)', background: 'none', border: 'none', cursor: 'pointer' }}>
                  {copied === 'auth' ? <Check size={11} /> : <Copy size={11} />} Copia
                </button>
              </div>
              <pre className="px-4 py-4 text-xs" style={{ color: '#a8b5c8', fontFamily: "'JetBrains Mono', monospace" }}>
                <span style={{ color: '#6b7280' }}>{'# Includi in ogni richiesta\n'}</span>
                {'X-API-Key: ostx_your_api_key_here'}
              </pre>
            </div>
            <p className="text-xs mt-3" style={{ color: 'rgba(229,229,229,0.3)' }}>
              Le chiavi hanno formato <code>ostx_...</code>. Non esporle in codice client-side o repository pubblici.
            </p>
          </section>

          {/* Rate Limits */}
          <section id="rate-limits">
            <h2 className="text-lg font-medium mb-4" style={{ color: '#e5e5e5' }}>Rate Limiting</h2>
            <p className="text-sm mb-4" style={{ color: 'rgba(229,229,229,0.55)', lineHeight: 1.7 }}>
              Il rate limiting è sliding window di 1 ora. Ogni risposta include gli header di stato.
            </p>
            <div className="rounded-sm overflow-hidden mb-4" style={{ border: '1px solid #1e1e1e' }}>
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ background: '#111', borderBottom: '1px solid #1e1e1e' }}>
                    {['Piano', 'Richieste/ora', 'Feature API'].map(h => (
                      <th key={h} className="text-left px-4 py-3 text-[10px] uppercase tracking-[2px]"
                        style={{ color: 'rgba(229,229,229,0.3)' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[
                    { plan: 'Essenziale', limit: '—', api: 'Non disponibile' },
                    { plan: 'Professional', limit: '2.000', api: 'Inclusa' },
                    { plan: 'Elite', limit: '10.000', api: 'Inclusa' },
                  ].map((r, i) => (
                    <tr key={r.plan} style={{ background: i % 2 === 0 ? '#0c0c0c' : '#0a0a0a', borderBottom: '1px solid #141414' }}>
                      <td className="px-4 py-3" style={{ color: '#e5e5e5' }}>{r.plan}</td>
                      <td className="px-4 py-3 font-mono" style={{ color: '#c9a84c' }}>{r.limit}</td>
                      <td className="px-4 py-3" style={{ color: r.api === 'Non disponibile' ? '#6b7280' : '#10b981' }}>{r.api}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <pre className="px-4 py-4 rounded-sm text-xs" style={{ background: '#0c0c0c', border: '1px solid #1e1e1e', color: '#a8b5c8', fontFamily: "'JetBrains Mono', monospace" }}>
              {`# Header di risposta rate limiting
X-RateLimit-Limit: 2000
X-RateLimit-Remaining: 1847
X-RateLimit-Reset: 1748563200    # Unix timestamp del prossimo reset

# Se superi il limite (HTTP 429):
{ "detail": "Rate limit superato: 2000 richieste/ora per il piano professional." }
# Retry-After: 3600`}
            </pre>
          </section>

          {/* Errors */}
          <section id="errors">
            <h2 className="text-lg font-medium mb-4" style={{ color: '#e5e5e5' }}>Gestione Errori</h2>
            <div className="rounded-sm overflow-hidden" style={{ border: '1px solid #1e1e1e' }}>
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ background: '#111', borderBottom: '1px solid #1e1e1e' }}>
                    {['Codice HTTP', 'Significato'].map(h => (
                      <th key={h} className="text-left px-4 py-3 text-[10px] uppercase tracking-[2px]" style={{ color: 'rgba(229,229,229,0.3)' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['200', 'Successo (o deduplicazione booking esistente)'],
                    ['201', 'Risorsa creata con successo'],
                    ['400', 'Parametri non validi'],
                    ['401', 'API Key mancante o non valida'],
                    ['403', 'API non abilitata per questo piano'],
                    ['404', 'Risorsa non trovata'],
                    ['409', 'Conflitto (es. booking già annullata)'],
                    ['422', 'Validation error (body malformato)'],
                    ['429', 'Rate limit superato'],
                    ['500', 'Errore interno — contatta il supporto'],
                  ].map(([code, desc], i) => (
                    <tr key={code} style={{ background: i % 2 === 0 ? '#0c0c0c' : '#0a0a0a', borderBottom: '1px solid #141414' }}>
                      <td className="px-4 py-2.5 font-mono text-xs" style={{ color: parseInt(code) >= 400 ? '#ef4444' : '#10b981' }}>{code}</td>
                      <td className="px-4 py-2.5 text-xs" style={{ color: 'rgba(229,229,229,0.6)' }}>{desc}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* Endpoint groups */}
          {ENDPOINTS.map(group => (
            <section key={group.group} id={group.group.toLowerCase().replace(/\s+/g, '-')}>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-1 h-6 rounded-full" style={{ background: group.color }} />
                <h2 className="text-lg font-medium" style={{ color: '#e5e5e5' }}>{group.group}</h2>
              </div>

              <div className="space-y-4">
                {group.items.map((item, idx) => {
                  const mc = METHOD_COLORS[item.method] || METHOD_COLORS.GET;
                  const endpointId = `${group.group}-${idx}`;
                  const isOpen = openEndpoint === endpointId;

                  return (
                    <div key={idx} className="rounded-sm overflow-hidden" style={{ border: '1px solid #1a1a1a' }}>
                      {/* Header row */}
                      <button
                        onClick={() => setOpenEndpoint(isOpen ? null : endpointId)}
                        data-testid={`endpoint-${item.method}-${item.path.replace(/\//g, '-')}`}
                        className="w-full flex items-center gap-3 px-4 py-3 text-left transition-colors"
                        style={{ background: isOpen ? '#101010' : '#0c0c0c' }}>
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded shrink-0"
                          style={{ background: mc.bg, color: mc.text, border: `1px solid ${mc.border}` }}>
                          {item.method}
                        </span>
                        <code className="text-sm flex-1" style={{ color: '#e5e5e5', fontFamily: "'JetBrains Mono', monospace" }}>
                          {item.path}
                        </code>
                        <span className="text-xs" style={{ color: 'rgba(229,229,229,0.35)' }}>{item.desc.split('.')[0]}</span>
                        <ChevronRight size={14} style={{ color: 'rgba(229,229,229,0.3)', transform: isOpen ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s' }} />
                      </button>

                      {isOpen && (
                        <div className="border-t" style={{ borderColor: '#1a1a1a' }}>
                          <div className="px-4 py-4">
                            <p className="text-sm mb-4" style={{ color: 'rgba(229,229,229,0.55)', lineHeight: 1.7 }}>{item.desc}</p>

                            {/* Curl example */}
                            <div className="mb-4">
                              <div className="flex items-center justify-between px-3 py-2 rounded-t" style={{ background: '#111' }}>
                                <span className="text-[10px] uppercase tracking-[2px]" style={{ color: 'rgba(229,229,229,0.3)' }}>
                                  <Code2 size={10} className="inline mr-1" /> cURL
                                </span>
                                <button onClick={() => copy(curlExample(item), endpointId + '-curl')}
                                  className="flex items-center gap-1 text-[10px]"
                                  style={{ color: 'rgba(229,229,229,0.35)', background: 'none', border: 'none', cursor: 'pointer' }}>
                                  {copied === endpointId + '-curl' ? <Check size={10} /> : <Copy size={10} />} Copia
                                </button>
                              </div>
                              <pre className="px-3 py-3 text-xs overflow-x-auto"
                                style={{ background: '#0a0a0a', color: '#a8b5c8', fontFamily: "'JetBrains Mono', monospace", borderBottomLeftRadius: '2px', borderBottomRightRadius: '2px' }}>
                                {curlExample(item)}
                              </pre>
                            </div>

                            {/* Response */}
                            {item.response && (
                              <div>
                                <p className="text-[10px] uppercase tracking-[2px] mb-2" style={{ color: 'rgba(229,229,229,0.3)' }}>Risposta (200/201)</p>
                                <pre className="px-3 py-3 text-xs overflow-x-auto rounded-sm"
                                  style={{ background: '#0a0a0a', border: '1px solid #1a1a1a', color: '#a8b5c8', fontFamily: "'JetBrains Mono', monospace" }}>
                                  {item.response}
                                </pre>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          ))}

          {/* Webhook & HMAC */}
          <section id="webhooks">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-1 h-6 rounded-full" style={{ background: '#c9a84c' }} />
              <h2 className="text-lg font-medium" style={{ color: '#e5e5e5' }}>Webhook & Notifiche HMAC</h2>
            </div>
            <p className="text-sm mb-6" style={{ color: 'rgba(229,229,229,0.55)', lineHeight: 1.7 }}>
              Shangrilux invia notifiche HTTP <strong style={{ color: '#e5e5e5' }}>outbound</strong> al tuo endpoint ogni volta che si verifica un evento (prenotazione, check-in, comando accesso, ecc.).
              Ogni richiesta include una firma <code style={{ color: '#c9a84c' }}>HMAC-SHA256</code> che puoi verificare per garantire l'autenticità del payload.
            </p>

            {/* Headers inviati da Shangrilux */}
            <div className="mb-6">
              <p className="text-sm font-medium mb-3" style={{ color: '#e5e5e5' }}>Header inviati da Shangrilux</p>
              <div className="rounded-sm overflow-hidden" style={{ border: '1px solid #1e1e1e' }}>
                <table className="w-full text-xs">
                  <thead>
                    <tr style={{ background: '#111', borderBottom: '1px solid #1e1e1e' }}>
                      {['Header', 'Valore / Descrizione'].map(h => (
                        <th key={h} className="text-left px-4 py-3 text-[10px] uppercase tracking-[2px]"
                          style={{ color: 'rgba(229,229,229,0.3)' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ['X-Shangrilux-Signature', 'sha256=<hmac_hex> — Firma del body con il tuo secret'],
                      ['X-Shangrilux-Event', 'booking.created | booking.confirmed | booking.cancelled | ...'],
                      ['X-Shangrilux-Delivery', '<webhook_id>-<attempt> — ID univoco della consegna'],
                      ['Content-Type', 'application/json'],
                      ['User-Agent', 'Shangrilux-Webhook/1.0'],
                    ].map(([h, v], i) => (
                      <tr key={h} style={{ background: i % 2 === 0 ? '#0c0c0c' : '#0a0a0a', borderBottom: '1px solid #141414' }}>
                        <td className="px-4 py-2.5 font-mono" style={{ color: '#c9a84c' }}>{h}</td>
                        <td className="px-4 py-2.5" style={{ color: 'rgba(229,229,229,0.6)' }}>{v}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Python HMAC */}
            <div className="mb-5">
              <p className="text-sm font-medium mb-3" style={{ color: '#e5e5e5' }}>Verifica firma in Python</p>
              <div className="rounded-sm overflow-hidden" style={{ border: '1px solid #1e1e1e' }}>
                <div className="flex items-center justify-between px-4 py-2.5" style={{ background: '#111' }}>
                  <span className="text-[10px] uppercase tracking-[3px]" style={{ color: 'rgba(229,229,229,0.3)' }}>Python / FastAPI</span>
                  <button onClick={() => copy(HMAC_PYTHON, 'hmac-py')}
                    className="flex items-center gap-1 text-[10px]" style={{ color: 'rgba(229,229,229,0.35)', background: 'none', border: 'none', cursor: 'pointer' }}>
                    {copied === 'hmac-py' ? <Check size={11} /> : <Copy size={11} />} Copia
                  </button>
                </div>
                <pre className="px-4 py-4 text-xs overflow-x-auto"
                  style={{ background: '#0a0a0a', color: '#a8b5c8', fontFamily: "'JetBrains Mono', monospace" }}>
                  {HMAC_PYTHON}
                </pre>
              </div>
            </div>

            {/* Node.js HMAC */}
            <div className="mb-5">
              <p className="text-sm font-medium mb-3" style={{ color: '#e5e5e5' }}>Verifica firma in Node.js / Express</p>
              <div className="rounded-sm overflow-hidden" style={{ border: '1px solid #1e1e1e' }}>
                <div className="flex items-center justify-between px-4 py-2.5" style={{ background: '#111' }}>
                  <span className="text-[10px] uppercase tracking-[3px]" style={{ color: 'rgba(229,229,229,0.3)' }}>Node.js / Express</span>
                  <button onClick={() => copy(HMAC_NODE, 'hmac-node')}
                    className="flex items-center gap-1 text-[10px]" style={{ color: 'rgba(229,229,229,0.35)', background: 'none', border: 'none', cursor: 'pointer' }}>
                    {copied === 'hmac-node' ? <Check size={11} /> : <Copy size={11} />} Copia
                  </button>
                </div>
                <pre className="px-4 py-4 text-xs overflow-x-auto"
                  style={{ background: '#0a0a0a', color: '#a8b5c8', fontFamily: "'JetBrains Mono', monospace" }}>
                  {HMAC_NODE}
                </pre>
              </div>
            </div>

            {/* Events table */}
            <div>
              <p className="text-sm font-medium mb-3" style={{ color: '#e5e5e5' }}>Eventi supportati</p>
              <div className="rounded-sm overflow-hidden" style={{ border: '1px solid #1e1e1e' }}>
                <table className="w-full text-xs">
                  <thead>
                    <tr style={{ background: '#111', borderBottom: '1px solid #1e1e1e' }}>
                      {['Evento', 'Descrizione', 'Quando viene emesso'].map(h => (
                        <th key={h} className="text-left px-4 py-3 text-[10px] uppercase tracking-[2px]"
                          style={{ color: 'rgba(229,229,229,0.3)' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ['booking.created', 'Nuova prenotazione', 'Portale pubblico, API o pannello admin'],
                      ['booking.confirmed', 'Prenotazione confermata', 'Lo staff accetta la richiesta'],
                      ['booking.cancelled', 'Prenotazione cancellata', 'Cancellazione da qualsiasi fonte'],
                      ['booking.checkin', 'Check-in eseguito', 'Apertura serratura al check-in'],
                      ['access.command', 'Comando dispositivo', 'Lock/unlock da API o pannello'],
                      ['guest_key.generated', 'Chiave ospite generata', 'QR code / link condiviso'],
                      ['webhook.test', 'Test delivery', 'Test manuale dal pannello webhook'],
                    ].map(([ev, desc, when], i) => (
                      <tr key={ev} style={{ background: i % 2 === 0 ? '#0c0c0c' : '#0a0a0a', borderBottom: '1px solid #141414' }}>
                        <td className="px-4 py-2.5 font-mono" style={{ color: '#c9a84c' }}>{ev}</td>
                        <td className="px-4 py-2.5" style={{ color: 'rgba(229,229,229,0.7)' }}>{desc}</td>
                        <td className="px-4 py-2.5" style={{ color: 'rgba(229,229,229,0.45)' }}>{when}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="mt-4 p-3 rounded-sm text-xs" style={{ background: 'rgba(201,168,76,0.06)', border: '1px solid rgba(201,168,76,0.15)' }}>
              <span style={{ color: '#c9a84c', fontWeight: 600 }}>Tip: </span>
              <span style={{ color: 'rgba(229,229,229,0.5)' }}>
                Configura i webhook dal pannello admin → sezione "Webhook". Puoi rivedere ogni consegna nei Log e riprovare manualmente i fallimenti con il pulsante "Riprova".
              </span>
            </div>
          </section>

          {/* SDK Download */}
          <section id="sdk">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-1 h-6 rounded-full" style={{ background: '#10b981' }} />
              <h2 className="text-lg font-medium" style={{ color: '#e5e5e5' }}>SDK Ufficiali</h2>
            </div>
            <p className="text-sm mb-6" style={{ color: 'rgba(229,229,229,0.55)', lineHeight: 1.7 }}>
              Client pronti all'uso per Python e Node.js. Scarica il file, aggiungi la tua API Key e sei operativo in 5 minuti.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
              {[
                {
                  lang: 'Python',
                  tag: 'PY',
                  tagColor: '#3b82f6',
                  color: '#3b82f6',
                  file: '/sdk/shangrilux-python-sdk.py',
                  install: 'pip install requests',
                  usage: "from shangrilux_sdk import ShangriluxClient\nclient = ShangriluxClient(api_key='ostx_...')",
                  size: '~6KB',
                },
                {
                  lang: 'Node.js / Browser',
                  tag: 'JS',
                  tagColor: '#84cc16',
                  color: '#84cc16',
                  file: '/sdk/shangrilux-node-sdk.js',
                  install: '# Nessuna dipendenza (usa fetch nativa)',
                  usage: "const { ShangriluxClient } = require('./shangrilux-node-sdk');\nconst client = new ShangriluxClient({ apiKey: 'ostx_...' });",
                  size: '~5KB',
                },
              ].map(sdk => (
                <div key={sdk.lang} className="p-5 rounded-sm"
                  style={{ background: '#0c0c0c', border: '1px solid #1a1a1a' }}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className="w-8 h-8 rounded flex items-center justify-center text-[11px] font-bold shrink-0"
                        style={{ background: `${sdk.tagColor}20`, color: sdk.tagColor, border: `1px solid ${sdk.tagColor}40` }}>
                        {sdk.tag}
                      </span>
                      <div>
                        <p className="text-sm font-medium" style={{ color: '#e5e5e5' }}>{sdk.lang}</p>
                        <p className="text-[10px]" style={{ color: 'rgba(229,229,229,0.35)' }}>{sdk.size}</p>
                      </div>
                    </div>
                    <a
                      href={sdk.file}
                      download
                      data-testid={`sdk-download-${sdk.lang.toLowerCase().split('/')[0].trim()}`}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-sm font-medium"
                      style={{ background: sdk.color, color: '#000' }}>
                      ↓ Scarica
                    </a>
                  </div>
                  <pre className="text-xs rounded-sm p-3 overflow-x-auto"
                    style={{ background: '#050505', color: '#a8b5c8', fontFamily: "'JetBrains Mono', monospace" }}>
                    <span style={{ color: '#6b7280' }}>{'# Install\n'}</span>
                    {sdk.install}{'\n\n'}
                    <span style={{ color: '#6b7280' }}>{'# Quick start\n'}</span>
                    {sdk.usage}
                  </pre>
                </div>
              ))}
            </div>
            <p className="text-xs" style={{ color: 'rgba(229,229,229,0.3)' }}>
              I SDK sono aggiornati alla versione API 1.0. Ogni file include esempi completi commentati.
            </p>
          </section>

          {/* Support */}
          <section className="border-t pt-10" style={{ borderColor: '#1a1a1a' }}>
            <p className="text-xs uppercase tracking-[4px] mb-3" style={{ color: '#c9a84c' }}>Supporto</p>
            <h2 className="text-lg font-medium mb-2" style={{ color: '#e5e5e5' }}>Hai bisogno di aiuto?</h2>
            <p className="text-sm mb-5" style={{ color: 'rgba(229,229,229,0.5)', lineHeight: 1.7 }}>
              Per integrazioni personalizzate, webhook avanzati o accesso a funzionalità Enterprise, contatta il team tecnico Shangrilux.
            </p>
            <div className="flex flex-wrap gap-3">
              <a href="mailto:info@shangrilux.com" className="px-5 py-2 text-sm rounded-sm"
                style={{ background: '#c9a84c', color: '#000', fontWeight: 600 }}>Contatta il team →</a>
              <a href="/blog" className="px-5 py-2 text-sm rounded-sm"
                style={{ border: '1px solid rgba(201,168,76,0.3)', color: '#c9a84c' }}>Guide & Integrazioni</a>
            </div>
          </section>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t py-10" style={{ borderColor: '#141414' }}>
        <div className="max-w-7xl mx-auto px-6 flex flex-col sm:flex-row flex-wrap items-center justify-between gap-4">
          <span style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: '16px', fontWeight: 300, color: 'rgba(229,229,229,0.3)' }}>Shangrilux</span>
          <div className="flex flex-wrap gap-5 justify-center">
            {[['/', 'Home'], ['/pricing', 'Prezzi'], ['/blog', 'Guide'], ['/marketplace', 'Marketplace'], ['/developers', 'API Docs']].map(([h, l]) => (
              <a key={h} href={h} className="text-xs hover:opacity-70 transition-opacity" style={{ color: 'rgba(229,229,229,0.3)' }}>{l}</a>
            ))}
          </div>
          <div className="flex flex-wrap gap-5 justify-center">
            {[['/privacy', 'Privacy'], ['/cookie', 'Cookie'], ['/terms', 'Termini']].map(([h, l]) => (
              <a key={h} href={h} className="text-xs hover:opacity-70 transition-opacity" style={{ color: 'rgba(229,229,229,0.25)' }}>{l}</a>
            ))}
          </div>
          <p className="text-xs" style={{ color: 'rgba(229,229,229,0.2)' }}>© {new Date().getFullYear()} Shangrilux · API v1.0</p>
        </div>
      </footer>
    </div>
  );
}
